import type * as React from "react";

import { Box } from "../../components/ui/box";
import { Icon } from "../../components/ui/icon";
import { Text } from "../../components/ui/text";
import type { Omittable } from "../../lib/props";

export type KanbanAddCardProps = Omittable<{
  /** Strip label, e.g. "Add Project". */
  label: string;
  /** Omit when a wrapping trigger owns the press. */
  onClick?: () => void;
  "aria-label"?: string;
  /** Merged by a wrapping popover trigger (`render={<KanbanAddCard/>}`). */
  ref?: React.Ref<HTMLButtonElement>;
}>;

/**
 * The "+ Add …" affordance for a column footer, as the next slot in the card
 * lane rather than a control floating in it: full column width, with rounded rectangular corners,
 * with content on the cards' leading line.
 * Render it from `renderColumnFooter`.
 */
export function KanbanAddCard({
  label,
  onClick,
  "aria-label": ariaLabel,
  ...props
}: KanbanAddCardProps) {
  return (
    <Box<"button">
      as="button"
      direction="row"
      align="center"
      gap="sm"
      width="100%"
      height={32}
      // The 8px inset puts the plus on the card bodies' leading line.
      rounded="md"
      paddingX="sm"
      hoverBackground
      onClick={onClick}
      aria-label={ariaLabel ?? label}
      className="cursor-pointer"
      {...props}
    >
      <Icon name="plus" size="md" color="muted" />
      <Text size="sm" variant="muted">
        {label}
      </Text>
    </Box>
  );
}
